import { LitElement, css, html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { makeCustomEvent, EVENT } from '../../../util/events';
import { consume } from '@lit/context';
import { formDataContext } from '../../../contexts/formData';
import { RateQuoteFormData } from '../../../util/form';
import '../../../components/continue-button';
import '../../../components/back-button';
import { subsidyTooltip } from '../../../util/tooltips';
import { breakpointContext } from '../../../contexts/breakpoint';
import { BREAKPOINT } from '../../../util/breakpoints';
import { classMap } from 'lit/directives/class-map.js';
import { sourceContext } from '../../../contexts/source';
import { SOURCE } from '../../../util/source';
import { FormValidationMessages } from '../../../enums/textConstantEnums';

type LitehouseRadioGroupElement = HTMLElement & {
    value: string;
    change: (value: string) => void;
};

@customElement('rq-step-assistance')
export class RateQuoteAssistance extends LitElement {
    static styles = css`
        .wrapper {
            display: flex;
            flex-direction: column;
            gap: 24px;
        }
        .income-wrapper {
            position: relative;
        }
        .dollar-sign {
            position: absolute;
            left: 6px;
            top: 84px;
            transform: translateY(-50%);
        }
        .skip-btn {
            display: grid;
            grid-template-columns: 1fr 1fr 1fr;
            gap: 4px;
        }
        .skip-icon {
            display: flex;
            justify-content: start;
        }
        .icon-padding {
            padding-top: 1px;
        }
    `;

    connectedCallback() {
        super.connectedCallback();
        this.addEventListener('lh-change', this.listenerHandler as EventListener);
        this.addEventListener('lh-input', this.listenerHandler as EventListener);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('lh-change', this.listenerHandler as EventListener);
        this.removeEventListener('lh-input', this.listenerHandler as EventListener);
    }

    @consume({ context: formDataContext, subscribe: true })
    data!: RateQuoteFormData;

    @consume({ context: breakpointContext, subscribe: true })
    breakpoint!: BREAKPOINT;

    @consume({ context: sourceContext })
    source!: SOURCE;

    private lastSyncedSubsidyCheck: boolean | undefined = undefined;
    private incomeHasInvalidChars = false;

    get isMobile() {
        return this.breakpoint === BREAKPOINT.PHONE || this.breakpoint === BREAKPOINT.TABLET;
    }

    updated(changedProps: Map<string | number | symbol, unknown>) {
        super.updated(changedProps);
        // Sync form values only if they've changed (not on every render)
        if (this.data.subsidyCheck !== undefined && this.data.subsidyCheck !== this.lastSyncedSubsidyCheck) {
            const subsidyRadioGroup = this.shadowRoot?.querySelector(
                'lh-radio-group[name="subsidyCheck"]'
            ) as LitehouseRadioGroupElement | null;
            if (subsidyRadioGroup?.change) {
                const newValue = String(this.data.subsidyCheck);
                if (subsidyRadioGroup.value !== newValue) {
                    subsidyRadioGroup.change(newValue);
                    this.lastSyncedSubsidyCheck = this.data.subsidyCheck;
                }
            }
        }
    }

    private listenerHandler = (e: Event) => {
        const event = e as CustomEvent<{ name: keyof RateQuoteFormData; value: unknown }>;
        const { name, value } = event.detail;
        const booleanVal = (value as string).toLowerCase() === 'true';
        let val = name === 'subsidyCheck' ? booleanVal : value;

        if (name === 'income') {
            const target = event.target as HTMLInputElement;
            const nativeInputValue = (target as unknown as { shadowRoot?: ShadowRoot }).shadowRoot?.querySelector(
                'input'
            )?.value;
            const rawIncomeValue =
                typeof nativeInputValue === 'string'
                    ? nativeInputValue
                    : typeof value === 'string'
                      ? value
                      : `${value}`;

            this.incomeHasInvalidChars = /[^\d]/.test(rawIncomeValue);

            const replacedVal = `${rawIncomeValue}`.replace(/\D/g, '');
            const valNum = Math.abs(Number(replacedVal));
            if (replacedVal && !isNaN(valNum)) {
                const valString = valNum.toString();
                val = valString;
            } else {
                val = '';
            }
        }

        const updatedForm: RateQuoteFormData = {
            ...this.data,
            [name]: val,
        };
        if (name === 'subsidyCheck' && !booleanVal) {
            delete updatedForm.houseSize;
            delete updatedForm.income;
            this.incomeHasInvalidChars = false;
        }
        this.dispatchEvent(makeCustomEvent(EVENT.UPDATE_FORM, { data: updatedForm }));
    };
    get houseSizeInput() {
        if (!this.data.subsidyCheck) return nothing;
        return html`<lh-input
            type="number"
            min="0"
            max="99"
            name="houseSize"
            default-value=${ifDefined(this.data.houseSize?.toString())}
        >
            How many people live in your household?
        </lh-input>`;
    }

    get incomeInput() {
        if (!this.data.subsidyCheck) return nothing;
        return html`
            <div class="income-wrapper">
                <span class="dollar-sign"><lh-typography>$</lh-typography></span>
                <lh-input
                    id="income-input"
                    min="0"
                    name="income"
                    default-value=${ifDefined(this.data.income?.toString())}
                    error-message=${this.incomeHasInvalidChars ? FormValidationMessages.NumbersOnly : ''}
                >
                    What's your estimated household income, before taxes?
                </lh-input>
            </div>
        `;
    }

    get disableContinue() {
        if (this.data.subsidyCheck === undefined) return true;
        return (
            this.data.subsidyCheck &&
            (!this.data.houseSize || !this.data.income || isNaN(Number(this.data.income)) || this.incomeHasInvalidChars)
        );
    }

    render() {
        const subsidyCheckValue = this.data.subsidyCheck === undefined ? undefined : String(this.data.subsidyCheck);

        return html`
            <div class="wrapper">
                <lh-typography type="heading" selector="h5" size=${this.isMobile ? 'xxl' : 'lg'}>
                    Want to check if you qualify for lower premiums?
                </lh-typography>
                <lh-typography>
                    You could be eligible for a subsidy, which can lower insurance costs depending on your income and
                    household size.
                </lh-typography>
                <lh-radio-group required name="subsidyCheck" default-value=${ifDefined(subsidyCheckValue)}>
                    <lh-group-legend slot="legend"
                        >Do you want to check if you qualify for a
                        <lh-tooltip description=${subsidyTooltip}>subsidy?</lh-tooltip></lh-group-legend
                    >
                    <lh-radio value="true">Yes</lh-radio>
                    <lh-radio value="false">No</lh-radio>
                </lh-radio-group>
                ${this.houseSizeInput} ${this.incomeInput}
                <rq-continue-button
                    .disabled=${this.disableContinue}
                    analytics-id="rate-quote:${this.source}:u65:step3:continue:button-filled"
                    submit
                ></rq-continue-button>
                <lh-button
                    analytics-id="rate-quote:${this.source}:u65:step3:skip:button-outlined"
                    full-width
                    @click=${() => {
                        const updatedForm: RateQuoteFormData = {
                            ...this.data,
                            subsidyCheck: false,
                        };
                        delete updatedForm.houseSize;
                        delete updatedForm.income;
                        this.dispatchEvent(makeCustomEvent(EVENT.UPDATE_FORM, { data: updatedForm }));
                        // TODO: we need to remove submit and bring back next step when top pick reinstated
                        // this.dispatchEvent(makeCustomEvent(EVENT.NEXT_STEP));
                        this.dispatchEvent(makeCustomEvent(EVENT.SUBMIT));
                    }}
                    variant="outlined"
                >
                    <div class="skip-btn">
                        <div></div>
                        <div>Skip</div>
                        <div class=${classMap({ 'skip-icon': true, 'icon-padding': !this.isMobile })}>
                            <lh-icon size="sm" name="caret-right"></lh-icon>
                        </div>
                    </div>
                </lh-button>
                <rq-back-button analytics-id="rate-quote:${this.source}:u65:step3:back:button-text"></rq-back-button>
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'rq-step-assistance': RateQuoteAssistance;
    }
}
